import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBiosite, trackEvent, resolveTheme, shapeRadius, evalBusinessHours } from '../api';
import { Instagram, Youtube, Twitter, Globe, Camera, Calendar, Music, ShoppingBag, MessageCircle, BookOpen, Download, QrCode, Share2, BadgeCheck, Link2, ExternalLink, Loader2, MapPin, Phone, Star, Utensils, Facebook, Clock } from 'lucide-react';
import PixModal from '../components/PixModal';
import MenuSheet from '../components/MenuSheet';
import BookingFlow from '../components/BookingFlow';
import Showcase from '../components/Showcase';

const DEFAULT_ICONS = {
  camera: Camera, calendar: Calendar, music: Music, 'shopping-bag': ShoppingBag,
  'message-circle': MessageCircle, globe: Globe, youtube: Youtube,
  'book-open': BookOpen, download: Download, 'link': Link2, 'map-pin': MapPin,
  phone: Phone, star: Star, utensils: Utensils, facebook: Facebook, instagram: Instagram, tiktok: Music
};
const TYPE_ICON = {
  whatsapp: MessageCircle, instagram: Instagram, tiktok: Music, facebook: Facebook,
  youtube: Youtube, maps: MapPin, phone: Phone, review: Star, menu: Utensils,
  appointment: Calendar, custom: Link2
};
const SOCIAL_ICONS = { instagram: Instagram, youtube: Youtube, twitter: Twitter, tiktok: Music, globe: Globe, facebook: Facebook };

function buildLinkHref(link) {
  if (link.type === 'whatsapp') {
    const num = (link.phone || '').replace(/\D/g, '');
    const q = link.message ? `?text=${encodeURIComponent(link.message)}` : '';
    return num ? `https://wa.me/${num}${q}` : (link.url || '#');
  }
  if (link.type === 'phone') {
    const num = (link.phone || '').replace(/\D/g, '');
    return num ? `tel:${num}` : (link.url || '#');
  }
  return link.url || '#';
}

export default function Biosite() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pixOpen, setPixOpen] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const viewTracked = useRef(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const d = await getBiosite(slug);
        if (active) setData(d);
        if (active && d && d.active !== false && !viewTracked.current) {
          viewTracked.current = true;
          trackEvent(slug, 'view', 'page');
        }
      } catch { if (active) setData(null); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [slug]);

  // tick every minute to refresh open/closed badge
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  if (loading) return <div className="min-h-screen grid place-items-center bg-neutral-50"><Loader2 className="animate-spin text-neutral-400" size={28} /></div>;
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-50 px-6">
        <h1 className="text-3xl font-bold text-neutral-900 font-display">Biosite não encontrado</h1>
        <p className="text-neutral-600 mt-2">O link <span className="font-mono">/{slug}</span> ainda não foi criado.</p>
        <Link to="/" className="mt-6 text-sm underline text-neutral-900">Voltar</Link>
      </div>
    );
  }

  if (data.active === false) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-50 px-6 text-center">
        <div className="w-16 h-16 rounded-full bg-neutral-200 grid place-items-center mb-4"><Clock className="text-neutral-500" size={28} /></div>
        <h1 className="text-2xl font-bold text-neutral-900 font-display">Perfil temporariamente indisponível</h1>
        <p className="text-neutral-600 mt-2 max-w-sm">O biosite <span className="font-mono">/{slug}</span> está pausado no momento. Volte mais tarde.</p>
      </div>
    );
  }

  const theme = resolveTheme(data);
  const radius = shapeRadius(data.buttonShape);
  const links = (data.links || []).filter((l) => l.enabled !== false);
  const pixList = (data.pixEnabled !== false ? (data.pix || []) : []).filter((p) => p.enabled !== false);
  const trackClick = (target) => trackEvent(slug, 'click', target);
  const hours = evalBusinessHours(data.businessHours, now);
  const schedulingEnabled = data.scheduling?.enabled && (data.scheduling?.services || []).some((s) => s.enabled !== false);
  const hasMenu = data.menuEnabled && (data.menu || []).some((c) => (c.products || []).some((p) => p.enabled !== false));

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: data.name, url }); } catch { /* cancel */ } }
    else { await navigator.clipboard.writeText(url); alert('Link copiado!'); }
  };

  const onLinkClick = (link, e) => {
    if (link.type === 'menu') { e.preventDefault(); setMenuOpen(true); trackClick('menu'); return; }
    if (link.type === 'appointment' && schedulingEnabled && !link.url) { e.preventDefault(); setBookingOpen(true); trackClick('appointment'); return; }
    trackClick(link.type && link.type !== 'custom' ? link.type : `link:${link.id}`);
  };

  return (
    <div className="min-h-screen w-full relative" style={{ background: theme.bg }}>
      {data.cover && (
        <div className="w-full h-40 sm:h-52 relative overflow-hidden" style={{ backgroundImage: `url(${data.cover})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20" />
        </div>
      )}

      <div className="max-w-xl mx-auto px-5 pb-24 relative" style={{ marginTop: data.cover ? -56 : 0, paddingTop: data.cover ? 0 : 40 }}>
        <div className="flex justify-end mb-2 relative z-10">
          <button onClick={share} className="rounded-full p-2 bg-black/25 backdrop-blur-sm hover:bg-black/40 transition text-white" aria-label="Compartilhar"><Share2 size={18} /></button>
        </div>

        <div className="flex flex-col items-center text-center fade-in-up relative z-10">
          <img src={data.avatar} alt={data.name} className="w-24 h-24 rounded-full object-cover avatar-ring border-[3px] border-white shadow-lg" />
          <h1 className="mt-3 text-xl font-bold font-display flex items-center gap-1.5" style={{ color: theme.text }}>
            @{data.slug}
            {data.verified && <BadgeCheck size={18} className="fill-blue-500 text-white" />}
          </h1>
          <p className="mt-1 text-sm opacity-90 max-w-xs" style={{ color: theme.text }}>{data.bio}</p>
          {hours && (
            <div className={`mt-3 inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3 py-1 ${hours.open ? 'bg-emerald-500/90 text-white' : 'bg-black/40 text-white'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${hours.open ? 'bg-white animate-pulse' : 'bg-neutral-300'}`} />
              {hours.label}{hours.detail ? ` • ${hours.detail}` : ''}
            </div>
          )}
        </div>

        <div className="mt-7 space-y-3">
          {schedulingEnabled && (
            <button
              onClick={() => { setBookingOpen(true); trackClick('appointment'); }}
              className="bio-btn fade-in-up flex items-center justify-between gap-3 w-full px-5 py-4 font-semibold text-[15px]"
              style={{ background: theme.button, color: theme.buttonText, borderRadius: radius }}
            >
              <Calendar size={20} className="shrink-0" />
              <span className="flex-1 text-center">{data.scheduling.buttonLabel || 'Agendar Horário'}</span>
              <span className="text-[10px] font-bold tracking-widest opacity-70">AGENDAR</span>
            </button>
          )}
          {hasMenu && (
            <button onClick={() => { setMenuOpen(true); trackClick('menu'); }}
              className="bio-btn fade-in-up flex items-center justify-between gap-3 w-full px-5 py-4 font-semibold text-[15px]"
              style={{ background: theme.button, color: theme.buttonText, borderRadius: radius }}>
              <Utensils size={20} className="shrink-0" />
              <span className="flex-1 text-center">{data.menuButtonLabel || 'Ver Cardápio'}</span>
              <span className="text-[10px] font-bold tracking-widest opacity-70">MENU</span>
            </button>
          )}

          {links.map((link, idx) => {
            const Icon = TYPE_ICON[link.type] || DEFAULT_ICONS[link.icon] || Link2;
            const isOutline = link.style === 'outline';
            const isActionBtn = link.type === 'menu' || (link.type === 'appointment' && schedulingEnabled && !link.url);
            const href = buildLinkHref(link);
            return (
              <a key={link.id} href={isActionBtn ? '#' : href}
                onClick={(e) => onLinkClick(link, e)}
                target={isActionBtn ? undefined : '_blank'} rel="noopener noreferrer"
                className="bio-btn fade-in-up flex items-center justify-between gap-3 w-full px-5 py-4 font-semibold text-[15px]"
                style={{
                  background: isOutline ? 'transparent' : theme.button,
                  color: isOutline ? theme.text : theme.buttonText,
                  border: isOutline ? `2px solid ${theme.text}` : 'none',
                  borderRadius: radius, animationDelay: `${idx * 60}ms`
                }}>
                <Icon size={20} className="shrink-0" />
                <span className="flex-1 text-center">{link.title}</span>
                {isActionBtn ? <Icon size={16} className="opacity-50 shrink-0" /> : <ExternalLink size={16} className="opacity-50 shrink-0" />}
              </a>
            );
          })}

          {pixList.map((pix, idx) => (
            <button key={pix.id} onClick={() => { setPixOpen(pix); trackClick('pix'); }}
              className="bio-btn fade-in-up flex items-center justify-between gap-3 w-full px-5 py-4 font-semibold text-[15px]"
              style={{ background: theme.button, color: theme.buttonText, border: `2px dashed ${theme.buttonText}20`, borderRadius: radius, animationDelay: `${(links.length + idx) * 60}ms` }}>
              <QrCode size={20} className="shrink-0" />
              <span className="flex-1 text-center">
                {pix.title}
                {pix.amount > 0 && <span className="ml-2 text-xs opacity-70">R$ {Number(pix.amount).toFixed(2).replace('.', ',')}</span>}
              </span>
              <span className="text-[10px] font-bold tracking-widest opacity-70">PIX</span>
            </button>
          ))}
        </div>

        {data.socials && data.socials.length > 0 && (
          <div className="mt-10 flex items-center justify-center gap-4">
            {data.socials.map((s, i) => {
              const SIcon = SOCIAL_ICONS[s.platform] || Globe;
              return (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                  onClick={() => trackClick(s.platform)}
                  className="p-2.5 rounded-full bg-black/15 backdrop-blur hover:bg-black/30 transition"
                  style={{ color: theme.text }} aria-label={s.platform}>
                  <SIcon size={20} />
                </a>
              );
            })}
          </div>
        )}

        <Showcase showcase={data.showcase} theme={theme} onItemClick={(it) => trackClick(`showcase:${it.id}`)} />

        <div className="mt-12 flex justify-center">
          <Link to="/" className="text-xs opacity-70 hover:opacity-100 transition" style={{ color: theme.text }}>feito com Biolink•br</Link>
        </div>
      </div>

      {pixOpen && <PixModal pix={pixOpen} onClose={() => setPixOpen(null)} merchantName={data.name} />}
      <MenuSheet open={menuOpen} onClose={() => setMenuOpen(false)} biosite={data} onProductClick={(p) => trackClick(`product:${p.id}`)} />
      <BookingFlow open={bookingOpen} onClose={() => setBookingOpen(false)} biosite={data} />
    </div>
  );
}
